// UX-1 — shared append-only site-evidence photo panel (PR / GRN / PMC / WP).
// Photos can never be replaced or deleted; late uploads carry an amber
// "Added after posting" label straight from the server metadata.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Eye, Clock } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

export function EvidencePhotos({
  title = "Site Photos", photos = [], max = 6, canUpload,
  uploadUrl, photoUrl, onChanged, testidPrefix = "photos",
}) {
  const [list, setList] = useState(photos || []);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setList(photos || []); }, [photos]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const small = await compressImage(f);
      const fd = new FormData();
      fd.append("file", small);
      fd.append("caption", caption.trim());
      const r = await api.post(uploadUrl, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.duplicate) {
        toast.info("Identical photo already attached — no new slot consumed");
      } else {
        toast.success("Photo attached");
        setList((ls) => [...ls, r.data]);
        setCaption("");
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally { setBusy(false); }
  };

  const view = async (p) => {
    try {
      const res = await api.get(photoUrl(p.id), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open photo"); }
  };

  return (
    <div className="text-xs" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1">
          <Camera size={11} /> {title}{" "}
          <span data-testid={`${testidPrefix}-count`}>({list.length}/{max})</span>
        </div>
        {canUpload && list.length < max && (
          <div className="flex items-center gap-1">
            <input data-testid={`${testidPrefix}-caption`} value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="caption (optional)"
              className="border border-slate-300 rounded px-1.5 py-0.5 text-[10px] w-36" />
            <label className={`text-[11px] font-bold text-[#F97316] cursor-pointer hover:underline ${busy ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`${testidPrefix}-upload-label`}>
              {busy ? "Uploading…" : "+ Add photo"}
              <input data-testid={`${testidPrefix}-input`} type="file"
                accept="image/*" className="hidden" onChange={upload} />
            </label>
          </div>
        )}
      </div>
      {list.length === 0
        ? <div className="text-slate-400 mt-1" data-testid={`${testidPrefix}-empty`}>No photos</div>
        : list.map((p) => (
          <div key={p.id} className="flex items-center gap-2 mt-1 flex-wrap"
            data-testid={`${testidPrefix}-row-${p.id}`}>
            <button onClick={() => view(p)}
              className="text-sky-700 hover:underline inline-flex items-center gap-1">
              <Eye size={11} /> {p.filename}
            </button>
            {p.caption && <span className="text-slate-500 italic">“{p.caption}”</span>}
            <span className="text-[9px] text-slate-400">
              {(p.uploaded_at || "").slice(0, 16).replace("T", " ")} · {p.uploaded_by}
            </span>
            {p.added_after_posting && (
              <span data-testid={`${testidPrefix}-late-${p.id}`}
                className="inline-flex items-center gap-0.5 bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded">
                <Clock size={9} /> Added after posting
              </span>
            )}
          </div>
        ))}
      <div className="text-[9px] text-slate-400 mt-1">
        Append-only evidence · photos cannot be replaced or deleted · location metadata stripped on upload
      </div>
    </div>
  );
}
